/**
 * Slash-Befehle für Markdown-Textfelder. Wird vom großen Editor und vom
 * Hausaufgaben-Dialog gemeinsam genutzt, damit beide dieselben Bausteine
 * (und denselben Parser) anbieten.
 */

import { h } from "@/lib/dom";
import { insertSnippet } from "./format";
import type { MdField } from "./field";

export interface Cmd {
  label: string;
  keywords: string;
  snippet: string;
  action?: "table" | "photo" | "pdfpage" | "sparkles" | "sketch" | "term" | "tree";
}

export function todayStr(): string {
  return new Date().toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Textbausteine an einer Stelle – damit Slash-Menü und Toolbar garantiert
 * dasselbe einfügen.
 */
export const SNIPPETS = {
  heute: () => `## ${todayStr()}\n`,
  aufgabe: "[ ] ",
  h1: "# ",
  h2: "## ",
  h3: "### ",
  liste: "- ",
  // Container statt `>`-Blockquote (siehe markdown/index.ts, "zitat"): der
  // `>` löste bei markdown-it "lazy continuation" aus – eine Autorenzeile
  // ohne `>` direkt danach wurde ohne Leerzeile trotzdem noch zum Zitat
  // gerechnet. Im Container gibt es kein `>` mehr, das Problem existiert
  // strukturell nicht. Autor optional in der Kopfzeile: "::: zitat Goethe".
  zitat: "\n::: zitat\n¦\n:::\n",
  // Cursor direkt hinter die öffnenden Zaunzeichen: dort steht als Erstes
  // die Sprache (```java), erst danach der Code.
  codeblock: "\n```¦\n\n```\n",
  trennlinie: "\n---\n",
  toc: "\n[toc]\n",
  info: "\n::: info Hinweis\n¦\n:::\n",
  warning: "\n::: warning Achtung\n¦\n:::\n",
  example: "\n::: example Beispiel\n¦\n:::\n",
  definition: "\n::: definition Definition\n¦\n:::\n",
  summary: "\n::: summary Zusammenfassung\n¦\n:::\n",
  tafelbild: "\n::: tafelbild Tafelbild\n¦\n:::\n",
  aufklappbar: "\n::: aufklappbar Mehr anzeigen\n¦\n:::\n",
  karteikarte: "\n::: karteikarte Begriff\n¦\n---\nRückseite\n:::\n",
  katex: "\n$$\n¦\n$$\n",
  tikz: "\n::: tikz\n¦\\draw (0,0) -- (2,1);\n:::\n",
  dbml: "\n```dbml\nTable ¦schueler {\n  id integer [primary key]\n  name varchar\n}\n```\n",
  mermaid: "\n\n```mermaid\n¦graph TD;\n  A[Start] --> B[Ende];\n```\n\n",
  spalten: "\n::: spalten\n¦Linke Spalte\n\n|||\n\nRechte Spalte\n:::\n",
  spoiler: "||¦versteckter Text||",
  // Erklärung steht im Titel-Teil, nicht im Linkziel: Ziele duerfen laut
  // CommonMark keine rohen Leerzeichen enthalten, ein Satz als Titel schon
  // (siehe markdown/index.ts, Regel fuer href "def:").
  begriff: '[¦Begriff](def: "Erklärung")',
} as const;

export const SLASH_CMDS: Cmd[] = [
  { label: "Heute (Datum)", keywords: "heute datum tag", snippet: SNIPPETS.heute() },
  { label: "Foto / Bild", keywords: "foto bild kamera photo image", snippet: "", action: "photo" },
  { label: "Aufgabe (Haken)", keywords: "aufgabe checkbox haken todo liste", snippet: SNIPPETS.aufgabe },
  { label: "Tabelle (visuell)", keywords: "tabelle table", snippet: "", action: "table" },
  {
    label: "PDF-Seite (Verweis)",
    keywords: "pdf seite material verweis link arbeitsblatt",
    snippet: "",
    action: "pdfpage",
  },
  { label: "Überschrift 1", keywords: "h1 ueberschrift titel", snippet: SNIPPETS.h1 },
  { label: "Überschrift 2", keywords: "h2 ueberschrift", snippet: SNIPPETS.h2 },
  { label: "Überschrift 3", keywords: "h3 ueberschrift", snippet: SNIPPETS.h3 },
  { label: "Aufzählung", keywords: "liste list punkt", snippet: SNIPPETS.liste },
  { label: "Zitat", keywords: "zitat quote", snippet: SNIPPETS.zitat },
  { label: "Codeblock", keywords: "code block", snippet: SNIPPETS.codeblock },
  { label: "Trennlinie", keywords: "trennlinie linie hr", snippet: SNIPPETS.trennlinie },
  { label: "Inhaltsverzeichnis", keywords: "toc inhalt verzeichnis", snippet: SNIPPETS.toc },
  { label: "Info-Box", keywords: "info hinweis", snippet: SNIPPETS.info },
  { label: "Warnung", keywords: "warnung achtung warning", snippet: SNIPPETS.warning },
  { label: "Beispiel", keywords: "beispiel example", snippet: SNIPPETS.example },
  { label: "Definition", keywords: "definition begriff", snippet: SNIPPETS.definition },
  { label: "Zusammenfassung", keywords: "zusammenfassung summary", snippet: SNIPPETS.summary },
  { label: "Tafelbild", keywords: "tafelbild tafel skizze zeichnung", snippet: SNIPPETS.tafelbild },
  { label: "Aufklappbar", keywords: "aufklappbar details", snippet: SNIPPETS.aufklappbar },
  { label: "Karteikarte", keywords: "karteikarte flashcard", snippet: SNIPPETS.karteikarte },
  { label: "KaTeX-Gleichung", keywords: "katex mathe formel gleichung math", snippet: SNIPPETS.katex },
  { label: "TikZ-Grafik", keywords: "tikz grafik zeichnung", snippet: SNIPPETS.tikz },
  { label: "DBML-Datenbankschema", keywords: "dbml datenbank schema tabelle er", snippet: SNIPPETS.dbml },
  { label: "Mermaid-Diagramm", keywords: "mermaid diagramm flowchart graph", snippet: SNIPPETS.mermaid },
  {
    label: "Baumdiagramm (Wahrscheinlichkeit)",
    keywords: "baum baumdiagramm wahrscheinlichkeit pfad stochastik urne zufall ziehen",
    snippet: "",
    action: "tree",
  },
  { label: "Zwei Spalten", keywords: "spalten columns layout", snippet: SNIPPETS.spalten },
  { label: "Spoiler (Inline)", keywords: "spoiler verstecken", snippet: SNIPPETS.spoiler },
  { label: "Begriff mit Erklärung", keywords: "begriff definition erklaerung glossar tooltip popup", snippet: "", action: "term" },
];

/** Ein Eintrag im Container-Menü der Toolbar; `children` = Untermenü. */
export interface MenuEntry {
  label: string;
  snippet?: string;
  action?: "term" | "tree";
  children?: MenuEntry[];
}

/**
 * Container-Menü der Editor-Toolbar. Die einfachen Callout-Boxen stecken in
 * einem Untermenü, damit die Liste kurz bleibt.
 */
export const CONTAINER_MENU: MenuEntry[] = [
  {
    label: "Box",
    children: [
      { label: "Info", snippet: SNIPPETS.info },
      { label: "Warnung", snippet: SNIPPETS.warning },
      { label: "Beispiel", snippet: SNIPPETS.example },
      { label: "Definition", snippet: SNIPPETS.definition },
      { label: "Zusammenfassung", snippet: SNIPPETS.summary },
      { label: "Tafelbild", snippet: SNIPPETS.tafelbild },
      { label: "Aufklappbar", snippet: SNIPPETS.aufklappbar },
    ],
  },
  { label: "Codeblock", snippet: SNIPPETS.codeblock },
  { label: "Karteikarte", snippet: SNIPPETS.karteikarte },
  { label: "KaTeX-Gleichung", snippet: SNIPPETS.katex },
  { label: "TikZ-Grafik", snippet: SNIPPETS.tikz },
  { label: "DBML-Datenbankschema", snippet: SNIPPETS.dbml },
  { label: "Mermaid-Diagramm", snippet: SNIPPETS.mermaid },
  { label: "Baumdiagramm (Wahrscheinlichkeit)", action: "tree" },
  { label: "Zwei Spalten", snippet: SNIPPETS.spalten },
  { label: "Spoiler (inline)", snippet: SNIPPETS.spoiler },
  { label: "Begriff mit Erklärung", action: "term" },
];

/**
 * Bild auswählen. Ohne `capture`-Attribut bietet das Handy von sich aus
 * „Kamera oder Mediathek" an – ein Knopf reicht also für beides.
 */
export function pickImage(onFile: (file: File) => void): void {
  const input = h("input", {
    type: "file",
    accept: "image/*",
    style: "display:none",
    onchange: (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      input.remove();
      if (file) onFile(file);
    },
  }) as HTMLInputElement;
  document.body.appendChild(input);
  input.click();
}

/** Einfügen an der Cursorposition (undo-fähig über das Feld selbst). */
export function insertAtCursor(ta: MdField, text: string): void {
  ta.focus();
  ta.insert(text);
  ta.emitInput();
}

/** Steht Text markiert, wird ER zum Begriff (nur die Erklärung ist auszufüllen), sonst der Platzhalter. */
export function insertTermDef(ta: MdField): void {
  const term = ta.value.slice(ta.selectionStart, ta.selectionEnd);
  insertSnippet(ta, term ? `[${term}](def: "¦")` : SNIPPETS.begriff);
}

/**
 * Hängt das Slash-Menü an ein Textfeld. Das zurückgegebene Element muss der
 * Aufrufer irgendwo einhängen (es positioniert sich selbst absolut).
 *
 * Wichtig: vor eigenen keydown-Listenern aufrufen – solange das Menü offen
 * ist, verbraucht es Pfeiltasten/Enter/Escape per stopImmediatePropagation.
 */
export function attachSlashMenu(
  ta: MdField,
  opts: { cmds: Cmd[]; onAction?: (cmd: Cmd) => void }
): HTMLElement {
  const menu = h("div", { class: "slash-menu", style: "display:none" });
  let items: Cmd[] = [];
  let index = 0;
  let start = -1;

  const isOpen = () => menu.style.display !== "none";
  const hide = () => {
    menu.style.display = "none";
    start = -1;
  };

  const renderMenu = () => {
    menu.innerHTML = "";
    items.forEach((c, i) => {
      const item = h(
        "button",
        {
          type: "button",
          class: "slash-item" + (i === index ? " active" : ""),
          onmousedown: (e: MouseEvent) => {
            e.preventDefault();
            choose(c);
          },
        },
        c.label
      );
      menu.appendChild(item);
      if (i === index) requestAnimationFrame(() => item.scrollIntoView({ block: "nearest" }));
    });
  };

  const choose = (c: Cmd) => {
    const caret = ta.selectionStart;
    ta.focus();
    ta.setSelectionRange(start, caret);
    insertSnippet(ta, c.snippet);
    hide();
    if (c.action) opts.onAction?.(c);
  };

  /** Setzt das Menü unter den Cursor; klappt nach oben, wenn unten kein Platz ist. */
  const place = () => {
    const { left, bottom } = ta.caretCoords();
    menu.style.left = Math.min(left, window.innerWidth - 250) + "px";
    const space = window.innerHeight - bottom;
    if (space < 180) {
      menu.style.top = "auto";
      menu.style.bottom = window.innerHeight - bottom + 20 + "px";
    } else {
      menu.style.bottom = "auto";
      menu.style.top = bottom + 4 + "px";
    }
  };

  const update = () => {
    const before = ta.value.slice(0, ta.selectionStart);
    // Auch ein „/" ohne Buchstaben öffnet das Menü – dann steht die komplette
    // Liste da und lässt sich durchscrollen.
    const m = before.match(/(^|\s)\/([\wäöü]*)$/i);
    if (!m) return hide();
    const q = m[2].toLowerCase();
    start = ta.selectionStart - m[2].length - 1;
    items = opts.cmds.filter(
      (c) => !q || c.label.toLowerCase().includes(q) || c.keywords.includes(q)
    );
    if (!items.length) return hide();
    index = 0;
    renderMenu();
    menu.style.display = "block";
    menu.scrollTop = 0;
    place();
  };

  ta.on("input", update);
  ta.on("blur", () => window.setTimeout(hide, 120));
  // Scrollen im Text schließt das Menü nicht mehr, es wandert einfach mit.
  ta.on("scroll", () => {
    if (isOpen()) place();
  });
  ta.on("keydown", (e: KeyboardEvent) => {
    if (!isOpen()) return;
    const step = (delta: number) => {
      index = (index + delta + items.length) % items.length;
      renderMenu();
    };
    if (e.key === "ArrowDown") step(1);
    else if (e.key === "ArrowUp") step(-1);
    else if (e.key === "Enter" || e.key === "Tab") choose(items[index]);
    else if (e.key === "Escape") hide();
    else return;
    e.preventDefault();
    e.stopImmediatePropagation();
  });

  return menu;
}
